/**
 * Thirty onboarding quiz cards (deck fixture).
 *
 * Why checked in: the onboarding deck is stable across runs, so bundled thumbnails
 * improve load times and allow offline testing.
 *
 * How to regenerate: npx tsx tooling/onboardingDeckCandidates.ts --emit
 * This downloads images from the catalog and resizes them to 1200px JPEG q82.
 * The PICKS table in the tooling script hand-sets minutes and intensity.
 *
 * @generated
 */

export type DeckSlot = 'q1a'|'q1b'|'q2a-meat'|'q2b-meat'|'q2a-veg'|'q2b-veg'|'q3a-meat'|'q3b-meat'|'q3a-veg'|'q3b-veg';
export type Intensity = 'bold'|'mild';

export interface QuizDeckEntry {
  slot: DeckSlot;
  variant: 0|1|2;
  recipeId: string;
  title: string;
  titleEn: string;
  image: number; // require() result
  minutes: number;
  difficulty: 'facile'|'moyen'|'technique';
  cost: '$'|'$$'|'$$$';
  source: string;
  dietKind: 'meat'|'fish'|'veg';
  intensity: Intensity;
  rating: number|null;
  ratingCount: number|null;
  ingredientCount: number;
}

export const ONBOARDING_DECK: QuizDeckEntry[] = [
  {
    slot: 'q1a',
    variant: 0,
    recipeId: 'imported-1773866760270-7ezbnx2he',
    title: "Boulettes suédoises",
    titleEn: "Swedish Meatballs",
    image: require('../../assets/onboarding/deck/q1a-0.jpg'),
    minutes: 50,
    difficulty: 'moyen',
    cost: '$$',
    source: "Ricardo",
    dietKind: 'meat',
    intensity: 'mild',
    rating: 5,
    ratingCount: 276,
    ingredientCount: 13,
  },
  {
    slot: 'q1a',
    variant: 1,
    recipeId: 'ee51d372-5bde-4306-b23c-fbf2465f89f1',
    title: "Filets de porc aigres-doux aux pommes et quartiers de chou braisés",
    titleEn: "Sweet-and-Sour Pork Tenderloin With Apples and Braised Cabbage",
    image: require('../../assets/onboarding/deck/q1a-1.jpg'),
    minutes: 45,
    difficulty: 'moyen',
    cost: '$$',
    source: "Ricardo",
    dietKind: 'meat',
    intensity: 'mild',
    rating: 5,
    ratingCount: 72,
    ingredientCount: 12,
  },
  {
    slot: 'q1a',
    variant: 2,
    recipeId: 'nyt-1774393856140-2w8fm18gz',
    title: "Poulet rôti aux herbes, petits pois et carottes sur plaque",
    titleEn: "Sheet-Pan Herby Roast Chicken With Peas and Carrots",
    image: require('../../assets/onboarding/deck/q1a-2.jpg'),
    minutes: 45,
    difficulty: 'moyen',
    cost: '$$',
    source: "nytimes",
    dietKind: 'meat',
    intensity: 'mild',
    rating: 4,
    ratingCount: 441,
    ingredientCount: 9,
  },
  {
    slot: 'q1b',
    variant: 0,
    recipeId: '88187b90-a8ca-4cb8-8075-132f44a1f0b7',
    title: "Soupe de lentilles au cari, lait de coco et tomates",
    titleEn: "Curried Lentil Soup With Coconut and Tomatoes",
    image: require('../../assets/onboarding/deck/q1b-0.jpg'),
    minutes: 40,
    difficulty: 'moyen',
    cost: '$$',
    source: "bonappetit",
    dietKind: 'veg',
    intensity: 'mild',
    rating: 4.9,
    ratingCount: 267,
    ingredientCount: 12,
  },
  {
    slot: 'q1b',
    variant: 1,
    recipeId: 'imported-1773865795563-bzgn0ehpq',
    title: "Pâtes crémeuses aux pois chiches, épinards et romarin",
    titleEn: "Creamy Chickpea Pasta With Spinach and Rosemary",
    image: require('../../assets/onboarding/deck/q1b-1.jpg'),
    minutes: 30,
    difficulty: 'moyen',
    cost: '$$',
    source: "NYT Cooking",
    dietKind: 'veg',
    intensity: 'mild',
    rating: 5,
    ratingCount: 10281,
    ingredientCount: 13,
  },
  {
    slot: 'q1b',
    variant: 2,
    recipeId: 'nyt-1774275609932-h662yp',
    title: "Haricots blancs gratinés au chili croustillant",
    titleEn: "Cheesy Chile Crisp White Beans",
    image: require('../../assets/onboarding/deck/q1b-2.jpg'),
    minutes: 25,
    difficulty: 'moyen',
    cost: '$$',
    source: "NYT Cooking",
    dietKind: 'veg',
    intensity: 'mild',
    rating: 5,
    ratingCount: 2674,
    ingredientCount: 8,
  },
  {
    slot: 'q2a-meat',
    variant: 0,
    recipeId: '038b9ddb-ac3f-41cb-8144-f941d5b12554',
    title: "Filet de poisson blanc à la coriandre fraîche",
    titleEn: "White Fish Fillet With Fresh Cilantro",
    image: require('../../assets/onboarding/deck/q2a-meat-0.jpg'),
    minutes: 25,
    difficulty: 'moyen',
    cost: '$$',
    source: "joseedistasio",
    dietKind: 'meat',
    intensity: 'mild',
    rating: 4.5,
    ratingCount: 2,
    ingredientCount: 11,
  },
  {
    slot: 'q2a-meat',
    variant: 1,
    recipeId: 'imported-1773866835371-mxb4qb32m',
    title: "Sauté de crevettes au sésame",
    titleEn: "Sesame Shrimp Stir-Fry",
    image: require('../../assets/onboarding/deck/q2a-meat-1.jpg'),
    minutes: 25,
    difficulty: 'moyen',
    cost: '$$',
    source: "Ricardo",
    dietKind: 'meat',
    intensity: 'mild',
    rating: 5,
    ratingCount: 178,
    ingredientCount: 13,
  },
  {
    slot: 'q2a-meat',
    variant: 2,
    recipeId: '1be937fe-4a16-4196-a74f-d4f22e1b6afd',
    title: "Gyros au rôti de porc et à la mangue",
    titleEn: "Roast Pork and Mango Gyros",
    image: require('../../assets/onboarding/deck/q2a-meat-2.jpg'),
    minutes: 15,
    difficulty: 'moyen',
    cost: '$$',
    source: "Ricardo",
    dietKind: 'meat',
    intensity: 'mild',
    rating: 5,
    ratingCount: 1,
    ingredientCount: 7,
  },
  {
    slot: 'q2b-meat',
    variant: 0,
    recipeId: 'imported-1773867072642-bmrfu7107',
    title: "Pilons de poulet au paprika et au curcuma",
    titleEn: "Paprika and Turmeric Chicken Drumsticks",
    image: require('../../assets/onboarding/deck/q2b-meat-0.jpg'),
    minutes: 60,
    difficulty: 'moyen',
    cost: '$$',
    source: "Ricardo",
    dietKind: 'meat',
    intensity: 'mild',
    rating: 5,
    ratingCount: 107,
    ingredientCount: 7,
  },
  {
    slot: 'q2b-meat',
    variant: 1,
    recipeId: 'a8e31651-0020-477a-be0e-23a7c1f0ff41',
    title: "Boeuf bourguignon",
    titleEn: "Beef Bourguignon",
    image: require('../../assets/onboarding/deck/q2b-meat-1.jpg'),
    minutes: 150,
    difficulty: 'moyen',
    cost: '$$',
    source: "Ricardo",
    dietKind: 'meat',
    intensity: 'mild',
    rating: 5,
    ratingCount: 99,
    ingredientCount: 18,
  },
  {
    slot: 'q2b-meat',
    variant: 2,
    recipeId: '17438589-46b4-4df4-9053-a95238bf42e2',
    title: "Tacos de porc braisé",
    titleEn: "Braised Pork Tacos",
    image: require('../../assets/onboarding/deck/q2b-meat-2.jpg'),
    minutes: 205,
    difficulty: 'moyen',
    cost: '$$',
    source: "Ricardo",
    dietKind: 'meat',
    intensity: 'mild',
    rating: 5,
    ratingCount: 19,
    ingredientCount: 18,
  },
  {
    slot: 'q2a-veg',
    variant: 0,
    recipeId: 'imported-1773865794127-vx8j9s7dj',
    title: "Gratin de haricots blancs aux tomates",
    titleEn: "Cheesy White Bean-Tomato Bake",
    image: require('../../assets/onboarding/deck/q2a-veg-0.jpg'),
    minutes: 15,
    difficulty: 'moyen',
    cost: '$$',
    source: "NYT Cooking",
    dietKind: 'veg',
    intensity: 'mild',
    rating: 5,
    ratingCount: 17702,
    ingredientCount: 7,
  },
  {
    slot: 'q2a-veg',
    variant: 1,
    recipeId: 'nyt-1774275617270-mbifa2',
    title: "Salade de haricots blancs au fromage croustillant",
    titleEn: "White Bean Salad With Crispy Cheese",
    image: require('../../assets/onboarding/deck/q2a-veg-1.jpg'),
    minutes: 20,
    difficulty: 'moyen',
    cost: '$$',
    source: "NYT Cooking",
    dietKind: 'veg',
    intensity: 'mild',
    rating: 5,
    ratingCount: 286,
    ingredientCount: 12,
  },
  {
    slot: 'q2a-veg',
    variant: 2,
    recipeId: '4f771670-b17b-43a9-8ebd-93b0f60577ae',
    title: "Gnocchis poêlés au beurre miso et aux asperges",
    titleEn: "Skillet Gnocchi With Miso Butter and Asparagus",
    image: require('../../assets/onboarding/deck/q2a-veg-2.jpg'),
    minutes: 15,
    difficulty: 'moyen',
    cost: '$$',
    source: "NYT Cooking",
    dietKind: 'veg',
    intensity: 'mild',
    rating: 5,
    ratingCount: 7941,
    ingredientCount: 9,
  },
  {
    slot: 'q2b-veg',
    variant: 0,
    recipeId: 'katrine-1774447064475-h65vpphis',
    title: "Soupe de poireaux, pommes de terre et lentilles",
    titleEn: "Leek, Potato and Lentil Soup",
    image: require('../../assets/onboarding/deck/q2b-veg-0.jpg'),
    minutes: 60,
    difficulty: 'moyen',
    cost: '$$',
    source: "K pour Katrine",
    dietKind: 'veg',
    intensity: 'mild',
    rating: null,
    ratingCount: null,
    ingredientCount: 13,
  },
  {
    slot: 'q2b-veg',
    variant: 1,
    recipeId: 'imported-1773866975391-efovyneuc',
    title: "Soupe aux lentilles rouges à l'indienne",
    titleEn: "Indian-Style Red Lentil Soup",
    image: require('../../assets/onboarding/deck/q2b-veg-1.jpg'),
    minutes: 45,
    difficulty: 'moyen',
    cost: '$$',
    source: "Ricardo",
    dietKind: 'veg',
    intensity: 'mild',
    rating: 5,
    ratingCount: 129,
    ingredientCount: 11,
  },
  {
    slot: 'q2b-veg',
    variant: 2,
    recipeId: 'nyt-1774393679538-l2tdlenii',
    title: "Cari de courge et tofu au lait de coco sur plaque",
    titleEn: "Sheet-Pan Coconut Curry With Squash and Tofu",
    image: require('../../assets/onboarding/deck/q2b-veg-2.jpg'),
    minutes: 55,
    difficulty: 'moyen',
    cost: '$$',
    source: "nytimes",
    dietKind: 'veg',
    intensity: 'mild',
    rating: 5,
    ratingCount: 1672,
    ingredientCount: 11,
  },
  {
    slot: 'q3a-meat',
    variant: 0,
    recipeId: '71bdf27a-624c-4201-bff8-8be7799fc807',
    title: "Plaque de crevettes, haricots noirs et chorizo",
    titleEn: "Sheet-Pan Shrimp, Black Beans and Chorizo",
    image: require('../../assets/onboarding/deck/q3a-meat-0.jpg'),
    minutes: 27,
    difficulty: 'moyen',
    cost: '$$',
    source: "Ricardo",
    dietKind: 'meat',
    intensity: 'bold',
    rating: 5,
    ratingCount: 62,
    ingredientCount: 10,
  },
  {
    slot: 'q3a-meat',
    variant: 1,
    recipeId: 'troisfois-1774445753464-sr14zlsb4',
    title: "Planche de saucisses & de légumes grillés",
    titleEn: "Grilled Sausage and Vegetable Board",
    image: require('../../assets/onboarding/deck/q3a-meat-1.jpg'),
    minutes: 30,
    difficulty: 'moyen',
    cost: '$$',
    source: "Trois fois par jour",
    dietKind: 'meat',
    intensity: 'bold',
    rating: null,
    ratingCount: null,
    ingredientCount: 13,
  },
  {
    slot: 'q3a-meat',
    variant: 2,
    recipeId: '33fd783d-5dec-4ed6-9b89-f671243a8bc4',
    title: "Bœuf grillé à la citronnelle",
    titleEn: "Lemongrass Grilled Beef",
    image: require('../../assets/onboarding/deck/q3a-meat-2.jpg'),
    minutes: 40,
    difficulty: 'moyen',
    cost: '$$',
    source: "Ricardo",
    dietKind: 'meat',
    intensity: 'bold',
    rating: 4,
    ratingCount: 5,
    ingredientCount: 14,
  },
  {
    slot: 'q3b-meat',
    variant: 0,
    recipeId: 'imported-1773867094694-bcfdqenw5',
    title: "Saumon aux herbes et asperges à la plaque",
    titleEn: "Sheet-Pan Herbed Salmon With Asparagus",
    image: require('../../assets/onboarding/deck/q3b-meat-0.jpg'),
    minutes: 30,
    difficulty: 'moyen',
    cost: '$$',
    source: "Ricardo",
    dietKind: 'meat',
    intensity: 'mild',
    rating: 5,
    ratingCount: 102,
    ingredientCount: 9,
  },
  {
    slot: 'q3b-meat',
    variant: 1,
    recipeId: '7633d7d4-6f67-4c4b-afe2-7b98145514b7',
    title: "Poisson en croûte de noix, betteraves jaunes minute et huile de ciboulette",
    titleEn: "Nut-Crusted Fish With Quick Golden Beets",
    image: require('../../assets/onboarding/deck/q3b-meat-1.jpg'),
    minutes: 32,
    difficulty: 'moyen',
    cost: '$$',
    source: "Ricardo",
    dietKind: 'meat',
    intensity: 'mild',
    rating: 5,
    ratingCount: 29,
    ingredientCount: 12,
  },
  {
    slot: 'q3b-meat',
    variant: 2,
    recipeId: 'saved-1768328767916-ohhoaa',
    title: "Poisson au four aux olives et au gingembre",
    titleEn: "Baked Fish With Olives and Ginger",
    image: require('../../assets/onboarding/deck/q3b-meat-2.jpg'),
    minutes: 30,
    difficulty: 'moyen',
    cost: '$$',
    source: "NYT Cooking",
    dietKind: 'meat',
    intensity: 'mild',
    rating: 5,
    ratingCount: 3062,
    ingredientCount: 9,
  },
  {
    slot: 'q3a-veg',
    variant: 0,
    recipeId: '6c7d3b3b-c46b-4e68-869e-9b31fa6ef420',
    title: "Sauté de tofu aux noix de cajou",
    titleEn: "Cashew Tofu Stir-Fry",
    image: require('../../assets/onboarding/deck/q3a-veg-0.jpg'),
    minutes: 35,
    difficulty: 'moyen',
    cost: '$$',
    source: "Ricardo",
    dietKind: 'veg',
    intensity: 'bold',
    rating: 5,
    ratingCount: 203,
    ingredientCount: 14,
  },
  {
    slot: 'q3a-veg',
    variant: 1,
    recipeId: 'katrine-1774446689253-c7hi5hfg6',
    title: "Nouilles soba au tofu et brocoli épicés",
    titleEn: "Spicy Soba Noodles With Tofu and Broccoli",
    image: require('../../assets/onboarding/deck/q3a-veg-1.jpg'),
    minutes: 35,
    difficulty: 'moyen',
    cost: '$$',
    source: "K pour Katrine",
    dietKind: 'veg',
    intensity: 'bold',
    rating: null,
    ratingCount: null,
    ingredientCount: 12,
  },
  {
    slot: 'q3a-veg',
    variant: 2,
    recipeId: 'imported-1773866986111-0x3w0sfea',
    title: "Tacos végétariens",
    titleEn: "Vegetarian Tacos",
    image: require('../../assets/onboarding/deck/q3a-veg-2.jpg'),
    minutes: 35,
    difficulty: 'moyen',
    cost: '$$',
    source: "Ricardo",
    dietKind: 'veg',
    intensity: 'bold',
    rating: 5,
    ratingCount: 129,
    ingredientCount: 16,
  },
  {
    slot: 'q3b-veg',
    variant: 0,
    recipeId: 'imported-1773867018983-seaf1ic4i',
    title: "Boulettes de tofu",
    titleEn: "Tofu Meatballs",
    image: require('../../assets/onboarding/deck/q3b-veg-0.jpg'),
    minutes: 35,
    difficulty: 'moyen',
    cost: '$$',
    source: "Ricardo",
    dietKind: 'veg',
    intensity: 'mild',
    rating: 5,
    ratingCount: 119,
    ingredientCount: 13,
  },
  {
    slot: 'q3b-veg',
    variant: 1,
    recipeId: 'ca8e276b-d6eb-4af3-bfec-656ded816a6e',
    title: "Bols-repas au tofu laqué",
    titleEn: "Glazed Tofu Bowls",
    image: require('../../assets/onboarding/deck/q3b-veg-1.jpg'),
    minutes: 32,
    difficulty: 'moyen',
    cost: '$$',
    source: "Radio-Canada",
    dietKind: 'veg',
    intensity: 'mild',
    rating: 4.5,
    ratingCount: 12,
    ingredientCount: 14,
  },
  {
    slot: 'q3b-veg',
    variant: 2,
    recipeId: 'd96d2e94-610e-4201-b67a-22b259764263',
    title: "Salade de légumineuses à la grecque",
    titleEn: "Greek-Style Bean Salad",
    image: require('../../assets/onboarding/deck/q3b-veg-2.jpg'),
    minutes: 15,
    difficulty: 'moyen',
    cost: '$$',
    source: "Trois fois par jour",
    dietKind: 'veg',
    intensity: 'mild',
    rating: 4.4,
    ratingCount: 30,
    ingredientCount: 14,
  },
];

export function deckEntry(slot: DeckSlot, variant: number): QuizDeckEntry {
  const e = ONBOARDING_DECK.find((x) => x.slot === slot && x.variant === variant);
  if (!e) throw new Error('No card for ' + slot + ' variant ' + variant);
  return e;
}
